import { useState } from 'react';
import { Button, Group, Select, Skeleton, Stack, Table, Text } from '@mantine/core';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError, type CuratorPiece } from '../../lib/api';
import { useStudy } from '../../app/study';

export const CURATOR_PIECES_KEY = ['curator', 'pieces'] as const;

/**
 * F15 curator → Pieces: the scripted library, published or not (M6b).
 *
 * This screen exists because a piece is not usable the moment it is seeded. `seed-pieces.mjs`
 * writes rows that are untagged (so the reader would be a wall of unmarked text), unlevelled (so
 * the shelf cannot place them) and unwarmed (so the first play waits on Azure) — and publishing
 * is deliberately a separate, human act after all three. Those are the four buttons here.
 *
 * The order is the pipeline and the table shows where each row is in it:
 *
 *     seed (CLI)  →  Tag  →  Warm  →  Publish
 *
 * Deliberately undesigned, like its two siblings: a plain table, because the audience is one
 * person doing an operational job a handful of times a month.
 *
 * **Warm is one piece per call and there is no "warm all" button.** That is not an omission. The
 * app's Azure budget is 400k characters a month and it is SHARED with dictation, the Listen
 * queue, saved-line audio and every "hear it" tap; a button that warmed the whole shelf would
 * spend the month in one click, and the first symptom would not be an error anyone saw — it would
 * be a learner's dictation prompt quietly failing days later. `capRemaining` is reported after
 * every warm so the number is in front of you while you decide whether to do another.
 */
export function CuratorPieces() {
  const qc = useQueryClient();
  const study = useStudy();
  const profile = study.me.profiles[0] ?? null;
  const [kind, setKind] = useState<string>('all');
  const [published, setPublished] = useState<string>('all');
  const [note, setNote] = useState<string | null>(null);

  const langs = {
    targetLang: profile?.targetLang ?? 'ka',
    baseLang: profile?.baseLang ?? 'en',
  };
  const filters = {
    ...(kind === 'all' ? {} : { kind }),
    ...(published === 'all' ? {} : { published: published === 'yes' }),
  };

  const q = useInfiniteQuery({
    queryKey: [...CURATOR_PIECES_KEY, filters, langs],
    queryFn: ({ pageParam }) =>
      api.curatorPieces({ ...langs, ...filters, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const refresh = () => void qc.invalidateQueries({ queryKey: CURATOR_PIECES_KEY });

  const tag = useMutation({
    mutationFn: () => api.curatorPieceTag({}),
    onSuccess: (r) => {
      setNote(
        `Tagged ${r.tagged}, levelled ${r.levelled}, ${r.remaining} still waiting.` +
          (r.skipped.length ? ` Skipped: ${r.skipped.join('; ')}` : ''),
      );
      refresh();
    },
    onError: (e) => setNote(errorLine(e)),
  });

  /**
   * The library-wide sweep, and it is here because the per-row button cannot do this job.
   *
   * `story.level` is a cached percentile over `item.level`. Re-levelling the catalog — which is
   * what M7-i does — invalidates it for EVERY tagged piece at once, so the repair is a sweep and
   * clicking forty rows is not a workflow. Sending no `storyIds` is what makes the route sweep.
   * It costs one percentile query per piece and no vendor call, which is why there is no confirm
   * step and no count on it the way `Tag untagged` has one.
   */
  const relevelAll = useMutation({
    mutationFn: () => api.curatorPieceRelevel({}),
    onSuccess: (r) => {
      setNote(
        `Re-levelled ${r.levelled}, ${r.unchanged} already correct.` +
          (r.skipped.length ? ` Skipped: ${r.skipped.join('; ')}` : ''),
      );
      refresh();
    },
    onError: (e) => setNote(errorLine(e)),
  });

  const untagged = rows.filter((p) => p.kind !== 'culture' && p.tagged === 0).length;

  return (
    <Stack gap="sm">
      {/* The screen says what the three steps ARE. Without this the buttons are three verbs with
          no object: "tag" and "warm" mean nothing to anyone who did not write them. */}
      <Text fz="xs" c="dimmed" component="div">
        A seeded piece is not a readable one yet. Three steps, left to right:
        <Text component="div" fz="xs" c="dimmed" mt={4} pl="sm">
          <b>Tag</b> — run the resolver over its sentences. Every word becomes an item, which is
          what gives the reader its marks and the gloss sheet something to look up, and the piece’s{' '}
          <b>level</b> is measured from those items. Costs a little Haiku the first time and nothing
          afterwards; the lemma cache is shared across the whole library.
          <br />
          <b>Warm</b> — synthesise every sentence into the audio cache now, each speaker in their
          own voice, so the first learner to press play does not wait on Azure. Nothing depends on
          it: an unwarmed piece just synthesises as it plays.
          <br />
          <b>Publish</b> — make it visible on the shelf. Refuses anything untagged, unlevelled or
          unvoiceable, and tells you everything that is wrong at once.
          <br />
          <b>Re-level</b> — re-measure the level from the tags a piece already has, after the
          catalog’s levels have moved. No LLM call and no budget gate: it reads the tags rather than
          rewriting them, which is the whole difference from <b>Re-tag</b>.
        </Text>
      </Text>

      <Group gap="sm" align="end">
        <Select
          label="Kind"
          size="xs"
          w={140}
          value={kind}
          onChange={(v) => setKind(v ?? 'all')}
          data={[
            { value: 'all', label: 'all' },
            { value: 'story', label: 'story' },
            { value: 'dialogue', label: 'dialogue' },
            { value: 'culture', label: 'culture' },
          ]}
        />
        <Select
          label="Published"
          size="xs"
          w={140}
          value={published}
          onChange={(v) => setPublished(v ?? 'all')}
          data={[
            { value: 'all', label: 'all' },
            { value: 'yes', label: 'published' },
            { value: 'no', label: 'not yet' },
          ]}
        />
        <Button
          size="xs"
          variant="light"
          loading={tag.isPending}
          disabled={untagged === 0}
          onClick={() => {
            setNote(null);
            tag.mutate();
          }}
          // The count is over the rows on screen, and the response reports what is left across
          // the whole library — so a filtered view cannot quietly claim to have finished.
          title="Tags up to 5 untagged pieces per click"
        >
          {untagged === 0 ? 'Everything is tagged' : `Tag untagged (${untagged} here)`}
        </Button>
        <Button
          size="xs"
          variant="default"
          loading={relevelAll.isPending}
          onClick={() => {
            setNote(null);
            relevelAll.mutate();
          }}
          title="Re-measures every tagged piece's level from its existing tags. No LLM call."
        >
          Re-level all
        </Button>
      </Group>

      {note && (
        <Text fz="xs" c="dimmed" role="status">
          {note}
        </Text>
      )}

      {q.isPending ? (
        <Stack gap={6}>
          <Skeleton height={28} />
          <Skeleton height={28} />
          <Skeleton height={28} />
        </Stack>
      ) : q.isError ? (
        <Text fz="sm" c="dimmed">
          {errorLine(q.error)}
        </Text>
      ) : rows.length === 0 ? (
        <Text fz="sm" c="dimmed">
          No pieces. Seed them first: <code>node packages/db/scripts/seed-pieces.mjs</code>
        </Text>
      ) : (
        <Table striped highlightOnHover fz="xs" verticalSpacing={4}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Piece</Table.Th>
              <Table.Th>Kind</Table.Th>
              <Table.Th>Level</Table.Th>
              <Table.Th title="items the resolver found — the reader's marks come from these">
                Tagged
              </Table.Th>
              <Table.Th title="sentences whose audio is already in the cache">Audio</Table.Th>
              <Table.Th>Published</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((p) => (
              <PieceRow key={p.id} piece={p} onNote={setNote} onChanged={refresh} />
            ))}
          </Table.Tbody>
        </Table>
      )}

      {q.hasNextPage && (
        <Button size="xs" variant="subtle" onClick={() => void q.fetchNextPage()}>
          More
        </Button>
      )}
    </Stack>
  );
}

function PieceRow({
  piece,
  onNote,
  onChanged,
}: {
  piece: CuratorPiece;
  onNote: (s: string) => void;
  onChanged: () => void;
}) {
  const tag = useMutation({
    // `retag: true` so the button means the same thing in both states: an already-tagged piece
    // is re-resolved and its level re-measured, which is exactly what M7's curve re-anchor will
    // need to do to the whole library.
    mutationFn: () => api.curatorPieceTag({ storyIds: [piece.id], retag: true }),
    onSuccess: (r) => {
      onNote(
        r.tagged === 0
          ? `${piece.slug}: nothing resolved — none of its words are in the catalog yet`
          : `${piece.slug}: tagged${r.levelled ? ', level measured' : ''}` +
              (r.skipped.length ? ` · ${r.skipped.join('; ')}` : ''),
      );
      onChanged();
    },
    onError: (e) => onNote(`${piece.slug}: ${errorLine(e)}`),
  });

  /**
   * Re-level is NOT a cheaper Re-tag; it is a different act on the same row.
   *
   * Re-tag above re-runs the resolver: it deletes every `story_item` row, rewrites them, and pays
   * the lemmatizer for whatever is not in the shared cache — so it is the right button when the
   * TEXT or the resolver changed. This one re-runs only the measurement over the tags already
   * there, which is the right button when the CATALOG changed underneath them. It cannot spend
   * money and cannot be refused by the budget gate.
   */
  const relevel = useMutation({
    mutationFn: () => api.curatorPieceRelevel({ storyIds: [piece.id] }),
    onSuccess: (r) => {
      // The row re-renders with the new level, so the note carries the OLD one — otherwise the
      // screen shows a number and no evidence that anything moved.
      onNote(
        r.levelled > 0
          ? `${piece.slug}: level re-measured, was ${piece.level ?? '—'}`
          : r.unchanged > 0
            ? `${piece.slug}: level unchanged at ${piece.level ?? '—'}`
            : `${piece.slug}: ${r.skipped.join('; ')}`,
      );
      onChanged();
    },
    onError: (e) => onNote(`${piece.slug}: ${errorLine(e)}`),
  });

  const warm = useMutation({
    mutationFn: () => api.curatorPieceWarm(piece.id),
    onSuccess: (r) => {
      onNote(
        `${piece.slug}: ${r.synthesized} synthesized, ${r.cached} already cached` +
          (r.failed ? `, ${r.failed} failed` : '') +
          ` · ${r.chars} chars · ${r.capRemaining.toLocaleString()} left this month`,
      );
      onChanged();
    },
    onError: (e) => onNote(`${piece.slug}: ${errorLine(e)}`),
  });

  const publish = useMutation({
    mutationFn: () =>
      piece.publishedAt ? api.curatorPieceUnpublish(piece.id) : api.curatorPiecePublish(piece.id),
    onSuccess: () => {
      onNote(`${piece.slug}: ${piece.publishedAt ? 'unpublished' : 'published'}`);
      onChanged();
    },
    // The publish route refuses with every reason at once — untagged, unlevelled, a speaker with
    // no voice — so the whole detail is shown rather than a generic failure.
    onError: (e) => onNote(`${piece.slug}: ${errorLine(e)}`),
  });

  const audio = piece.kind === 'culture' ? '—' : `${piece.warmed}/${piece.sentences}`;

  return (
    <Table.Tr>
      <Table.Td>
        <Link to={`/read/${piece.id}`} style={{ color: 'var(--nn-text-strong)' }}>
          {piece.title ?? piece.slug ?? piece.id.slice(0, 8)}
        </Link>
        <Text fz={10} c="dimmed">
          {piece.slug}
          {piece.speakers.length > 0 ? ` · ${piece.speakers.join(' / ')}` : ''}
        </Text>
      </Table.Td>
      <Table.Td>{piece.kind}</Table.Td>
      <Table.Td>{piece.level ?? '—'}</Table.Td>
      <Table.Td>{piece.kind === 'culture' ? '—' : piece.tagged}</Table.Td>
      <Table.Td c={piece.warmed === piece.sentences ? undefined : 'dimmed'}>{audio}</Table.Td>
      <Table.Td>{piece.publishedAt ? '✓' : '—'}</Table.Td>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          {piece.kind !== 'culture' && (
            <>
              <Button
                size="compact-xs"
                variant={piece.tagged === 0 ? 'light' : 'subtle'}
                loading={tag.isPending}
                onClick={() => tag.mutate()}
              >
                {piece.tagged === 0 ? 'Tag' : 'Re-tag'}
              </Button>
              <Button
                size="compact-xs"
                // `default` (outlined) rather than `subtle`, so the two verbs next to each other
                // do not look like the same kind of act. They are not: one rewrites the tags and
                // costs, this one only re-reads them.
                variant="default"
                loading={relevel.isPending}
                disabled={piece.tagged === 0}
                onClick={() => relevel.mutate()}
                title="Re-measures the level from the existing tags. No LLM call, no budget gate."
              >
                Re-level
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                loading={warm.isPending}
                onClick={() => warm.mutate()}
              >
                {piece.warmed >= piece.sentences ? 'Re-warm' : 'Warm'}
              </Button>
            </>
          )}
          <Button
            size="compact-xs"
            variant={piece.publishedAt ? 'subtle' : 'light'}
            loading={publish.isPending}
            onClick={() => publish.mutate()}
          >
            {piece.publishedAt ? 'Unpublish' : 'Publish'}
          </Button>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

function errorLine(e: unknown): string {
  if (e instanceof ApiError) {
    // `problem.detail` is where the publish gate lists everything that is wrong at once.
    return e.problem.detail ? `${e.problem.title} — ${e.problem.detail}` : e.problem.title;
  }
  return 'Something went wrong.';
}
